import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { Badge, Button, Card, JsonBlock, KeyValue, QueryBoundary } from '../components/ui';
import { atLeast } from '../lib/roles';
import { dateTime } from '../lib/format';
import { jobTone, secs } from './Operations';

function duration(start: string | null, end: string | null): string {
  if (!start) return '—';
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  const s = Math.max(0, Math.round((b - a) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const jobId = Number(id);
  const { user } = useAuth();
  const canCancel = atLeast(user?.role, 'operator');
  const qc = useQueryClient();
  const preRef = useRef<HTMLPreElement>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.job(jobId),
    enabled: Number.isFinite(jobId),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'queued' || s === 'running' ? 1500 : false;
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelJob(jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job', jobId] }),
  });

  const worker = useQuery({
    queryKey: ['worker-status'],
    queryFn: api.workerStatus,
    refetchInterval: 15_000,
  });

  const j = q.data;
  const live = j?.status === 'queued' || j?.status === 'running';

  useEffect(() => {
    if (live && preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [j?.output, live]);

  return (
    <>
      <PageHeader
        title={`Job #${id}`}
        description={j ? j.kind : undefined}
        actions={
          <div className="flex items-center gap-2">
            {j && canCancel && live && (
              <Button size="sm" variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                {cancel.isPending ? 'Cancelling…' : 'Cancel'}
              </Button>
            )}
            <Link className="text-sm text-sky-400 hover:underline" to="/operations">
              ← operations
            </Link>
          </div>
        }
      />

      <QueryBoundary q={q}>
        {j && (
          <div className="space-y-5">
            {j.status === 'queued' && (
              <Card
                className={
                  worker.data?.online === false
                    ? 'border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-200'
                    : 'border-amber-900/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-200'
                }
              >
                {worker.data?.online === false ? (
                  <>
                    <strong>No worker is consuming the queue</strong> — this job will not start
                    until one is running. {worker.data.detail}
                  </>
                ) : (
                  <>
                    Waiting for a worker
                    {j.waiting_seconds != null && ` (${secs(j.waiting_seconds)} so far)`}.
                    {j.waiting_seconds != null && j.waiting_seconds > 60 && (
                      <> A wait this long usually means the worker is busy or wedged.</>
                    )}
                  </>
                )}
              </Card>
            )}
            <Card className="p-4">
              <KeyValue
                rows={[
                  ['Status', <Badge tone={jobTone(j.status)}>{j.status}</Badge>],
                  ['Exit code', j.exit_code ?? '—'],
                  ['Created', dateTime(j.created_at)],
                  ['Started', dateTime(j.started_at)],
                  ['Finished', dateTime(j.finished_at)],
                  ['Duration', duration(j.started_at, j.finished_at)],
                  ['By', j.created_by_email ?? '—'],
                ]}
              />
              {j.error && <p className="mt-3 text-sm text-red-400">{j.error}</p>}
            </Card>

            <Card className="p-4">
              <h2 className="mb-2 text-sm font-medium text-slate-300">Parameters</h2>
              <JsonBlock value={j.params} />
              {j.argv.length > 0 && (
                <pre className="mt-2 overflow-x-auto rounded border border-edge bg-surface p-2 text-xs text-slate-500">
                  python {j.argv.join(' ')}
                </pre>
              )}
            </Card>

            {j.artifacts.length > 0 && (
              <Card className="p-4">
                <h2 className="mb-2 text-sm font-medium text-slate-300">Artifacts</h2>
                <ul className="space-y-1.5 text-sm text-slate-300">
                  {j.artifacts.map((a) => (
                    <li key={a} className="flex items-center justify-between gap-3">
                      <code className="text-xs">
                        /artifacts/job-{j.id}/{a}
                      </code>
                      <Button
                        size="sm"
                        disabled={downloading === a}
                        onClick={() => {
                          setDownloading(a);
                          setDownloadError(null);
                          api
                            .downloadArtifact(j.id, a)
                            .catch((e) =>
                              setDownloadError(e instanceof Error ? e.message : 'download failed'),
                            )
                            .finally(() => setDownloading(null));
                        }}
                      >
                        {downloading === a ? 'Downloading…' : 'Download'}
                      </Button>
                    </li>
                  ))}
                </ul>
                {downloadError && <p className="mt-2 text-xs text-red-400">{downloadError}</p>}
                {j.kind === 'build_dataset' && j.artifacts.includes('dataset.json') && (
                  <p className="mt-2 text-xs text-slate-500">
                    Pick this in a <Link className="text-sky-400 hover:underline" to="/operations">Train models</Link> job.
                  </p>
                )}
              </Card>
            )}

            <Card className="p-4">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-medium text-slate-300">Output</h2>
                {live && <span className="text-xs text-sky-400">live…</span>}
              </div>
              <pre
                ref={preRef}
                className="max-h-[28rem] overflow-auto rounded border border-edge bg-black/40 p-3 text-xs leading-relaxed text-slate-300"
              >
                {j.output || (live ? 'waiting for output…' : '(no output)')}
              </pre>
            </Card>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
